import { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Checkbox,
  Switch,
  Divider,
  Typography,
  Box,
  Alert,
  CircularProgress,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTranslation } from "i18n-fe";
import { userDisplayName } from "common-fe";
import { useMemberRolesData, canGrantRole } from "../hooks/useMemberRolesData";

// Build the displayable role groups: each entitled product app, plus account
// governance (org_admin only — org_owner is never hand-assigned, org_member is
// implicit). §9.2 / §5.7.
function buildGroups(manifests, entitledApps) {
  const groups = entitledApps
    .filter((app) => manifests[app])
    .map((app) => ({
      app,
      manifest: manifests[app],
      roleKeys: Object.keys(manifests[app].roles || {}),
    }));
  if (manifests.account) {
    groups.push({
      app: "account",
      manifest: manifests.account,
      roleKeys: Object.keys(manifests.account.roles || {}).filter(
        (k) => k !== "org_owner" && k !== "org_member"
      ),
    });
  }
  return groups;
}

export default function MemberRolesDialog({
  orgId,
  member,
  open,
  onClose,
  onChanged,
  canManageOwner = false,
  onOwnerChange,
}) {
  const { t } = useTranslation("common");
  const { manifests, entitledApps, actorRoles, isSuper, loading, assignRole, revokeRole } =
    useMemberRolesData(orgId);

  const [roles, setRoles] = useState([]);
  const [owner, setOwner] = useState(false);
  const [ownerBusy, setOwnerBusy] = useState(false);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);

  // Initialize from the member when the dialog opens for a (different) member.
  // Keyed on userId so a background list refetch doesn't clobber in-dialog edits.
  useEffect(() => {
    setRoles(Array.isArray(member?.roles) ? member.roles : []);
    setOwner(member?.owner === true);
  }, [member?.userId, member?.owner]);

  if (!member) return null;

  const actor = { actorRoles, isSuper };
  const groups = buildGroups(manifests, entitledApps);
  const grantsFor = (manifest, roleDef) =>
    (roleDef.permissions || [])
      .map((p) => manifest.permissions?.[p]?.title || p)
      .join(" · ");

  const toggleOwner = async (checked) => {
    setError(null);
    setOwnerBusy(true);
    setOwner(checked); // optimistic
    try {
      if (onOwnerChange) await onOwnerChange(checked);
    } catch (err) {
      setOwner(!checked); // revert on failure
      setError(err.message);
    } finally {
      setOwnerBusy(false);
    }
  };

  const toggle = async (qualified, checked) => {
    setError(null);
    setBusy(qualified);
    try {
      const next = checked
        ? await assignRole(member.userId, qualified)
        : await revokeRole(member.userId, qualified);
      setRoles(Array.isArray(next) ? next : roles);
      if (onChanged) onChanged();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {t("memberRoles.title", {
          // #736 — name (first + last) before email, email before the raw
          // userId; never show email when a name is on record.
          member: userDisplayName(member) || member.userId,
        })}
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* Owner flag (owner-only) — making owners lives here, not in the row. */}
        {canManageOwner && (
          <Box sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={owner}
                  disabled={ownerBusy}
                  data-testid="member-owner-switch"
                  onChange={(e) => toggleOwner(e.target.checked)}
                />
              }
              label={
                <Box>
                  <Typography sx={{ color: "text.primary" }}>
                    {t("memberRoles.ownerLabel")}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    {t("memberRoles.ownerDescription")}
                  </Typography>
                </Box>
              }
            />
            <Divider sx={{ mt: 2 }} />
          </Box>
        )}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress />
          </Box>
        ) : groups.length === 0 ? (
          <Typography sx={{ color: "text.secondary" }}>
            {t("memberRoles.noAssignableRoles")}
          </Typography>
        ) : (
          groups.map((group) => {
            const grantable = group.roleKeys.filter((rk) =>
              canGrantRole(actor, `${group.app}:${rk}`)
            );
            if (grantable.length === 0) return null;
            return (
              <Accordion key={group.app} defaultExpanded data-testid={`role-group-${group.app}`}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography sx={{ color: "text.primary", textTransform: "capitalize" }}>
                    {group.app}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {grantable.map((rk) => {
                    const qualified = `${group.app}:${rk}`;
                    const def = group.manifest.roles[rk];
                    const checked = roles.includes(qualified);
                    return (
                      <Box key={qualified} sx={{ mb: 1.5 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={checked}
                              disabled={busy === qualified}
                              data-testid={`role-${qualified}`}
                              onChange={(e) => toggle(qualified, e.target.checked)}
                            />
                          }
                          label={
                            <Box>
                              <Typography sx={{ color: "text.primary" }}>
                                {def.title}
                              </Typography>
                              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                {def.description}
                              </Typography>
                            </Box>
                          }
                        />
                        {def.permissions?.length > 0 && (
                          <Typography
                            variant="caption"
                            sx={{ display: "block", ml: 4, color: "text.secondary" }}
                          >
                            {t("memberRoles.grants")}: {grantsFor(group.manifest, def)}
                          </Typography>
                        )}
                      </Box>
                    );
                  })}
                </AccordionDetails>
              </Accordion>
            );
          })
        )}

        {/* Direct grants (future §5.6) render distinctly from role-derived perms. */}
        <Box sx={{ mt: 2 }}>
          <Typography
            variant="caption"
            sx={{ fontStyle: "italic", color: "text.disabled" }}
            data-testid="direct-grants-placeholder"
          >
            {t("memberRoles.directGrantsNone")}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("memberRoles.close")}</Button>
      </DialogActions>
    </Dialog>
  );
}
